import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@env/environment';
import { LgValidationRepository } from '@service/lg-validation/lg-validation.repository';
import {
  LgValidationResponse,
  LgValidationStatus,
  ValidatedLgDetails
} from '@model/library/validated-lg.model';

/**
 * Backend response DTO for validated LG
 */
interface ValidatedLgBackendResponse {
  lgCode: string;
  specimenCode: string;
  specimenId: string;
  concentration: number;
  ratio260_280: number;
  ratio260_230: number;
  extractionMethod: string;
  wellLocation: string;
  technology: string;
  priority: string;
  isReprocess: boolean;
}

/**
 * HTTP status codes for error handling
 */
const HTTP_STATUS = {
  NOT_FOUND: 404,
  BAD_REQUEST: 400
} as const;

/**
 * HTTP Repository Implementation for LG Validation operations
 * Connects to backend endpoint:
 * - GET /specimens/by-lg/{lgCode}
 *
 * Used for validating LG codes when adding them to libraries
 */
@Injectable({
  providedIn: 'root'
})
export class HttpLgValidationRepository extends LgValidationRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}/specimens`;

  override validateLg(
    lgCode: string,
    technology?: string
  ): Observable<LgValidationResponse> {
    let url = `${this.BASE_URL}/by-lg/${lgCode}`;

    // Add technology query parameter if provided
    if (technology) {
      url += `?technology=${encodeURIComponent(technology)}`;
    }

    return this.http.get<ValidatedLgBackendResponse>(url).pipe(
      map((response) => this.mapToValidResponse(response)),
      catchError((error: HttpErrorResponse) =>
        of(this.mapErrorToResponse(error))
      )
    );
  }

  private mapToValidResponse(
    response: ValidatedLgBackendResponse
  ): LgValidationResponse {
    const details: ValidatedLgDetails = {
      lgCode: response.lgCode,
      specimenCode: response.specimenCode,
      specimenId: response.specimenId,
      concentration: response.concentration,
      ratio260_280: response.ratio260_280,
      ratio260_230: response.ratio260_230,
      extractionMethod: response.extractionMethod,
      wellLocation: response.wellLocation,
      technology: response.technology,
      priority: response.priority,
      isReprocess: response.isReprocess
    };

    return {
      status: LgValidationStatus.VALID,
      details,
      message: null
    };
  }

  private mapErrorToResponse(error: HttpErrorResponse): LgValidationResponse {
    if (error.status === HTTP_STATUS.NOT_FOUND) {
      return {
        status: LgValidationStatus.NOT_FOUND,
        details: null,
        message: 'El código LG no fue encontrado'
      };
    }

    if (error.status === HTTP_STATUS.BAD_REQUEST) {
      return {
        status: LgValidationStatus.INVALID_STATUS,
        details: null,
        message:
          error.error?.message ??
          'El código LG no está disponible para asignación'
      };
    }

    return {
      status: LgValidationStatus.INVALID_STATUS,
      details: null,
      message: 'Error al validar el código LG'
    };
  }
}
